import * as React from "react"
import { useTranslation } from "react-i18next"

import Details from "Catalog/Edit/shared/components/Details"
import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import StatefulField from "shared/components/StatefulField"
import useEditor from "shared/hooks/useEditor"
import useHandleImageUpload from "shared/hooks/useHandleImageUpload"
import useNavigateNoUpdate from "shared/hooks/useNavigateNoUpdate"
import { IDiscount } from "shared/hubrise/models"

import Commands from "./Commands"
import Pricing from "./Pricing"

const Content = ({ discount }: { discount: IDiscount }): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigateNoUpdate()

  const { editor } = useEditor()
  const { catalogNavigation } = useCatalogNavigation()
  const handleImageUpload = useHandleImageUpload()

  const handleChange =
    <TField extends keyof IDiscount>(field: TField, options?: { collapse?: boolean }) =>
    (value: IDiscount[TField]) =>
      editor.discount.update(discount.id, field, value, options)

  return (
    <>
      <Details.Topbar
        commands={<Commands discount={discount} />}
        onClose={() => navigate(catalogNavigation.editPath("discounts"))}
      >
        <StatefulField.Title
          name="discount-name"
          initialValue={discount.name}
          placeholder={t("discount.name.placeholder")}
          onChange={handleChange("name")}
          validate={(name) => (name.length === 0 ? t("validations.not_empty") : undefined)}
        />
      </Details.Topbar>

      <StatefulField.Input
        name="discount-ref"
        initialValue={discount.ref}
        label={t("discount.ref.label")}
        withMargin
        onChange={handleChange("ref")}
      />

      <Pricing discount={discount} />

      <StatefulField.TextEditor
        name="discount-description"
        initialValue={discount.description}
        label={t("discount.description.label")}
        withMargin
        placeholder={t("discount.description.placeholder")}
        onChange={handleChange("description")}
      />

      <StatefulField.ImagesHolder
        name="discount-images"
        initialValue={discount.imageHrIds}
        label={t("discount.images")}
        withMargin
        onChange={handleChange("imageHrIds", { collapse: false })}
        onUpload={handleImageUpload}
        imagePathResolver={(imageHrId) => catalogNavigation.imagePath(imageHrId)}
        modalTitle={discount.name}
      />
    </>
  )
}

export default React.memo(Content)
